import { getToken } from '@/utils/KeycloakUtils'
import type { CsrfResponse } from '@/types/account-management/AccountManagementTypes'
import {
  AccountActivationAPIApi,
  AccountRESTServiceAPIApi,
  APIsForChangingEmailOfAccountApi,
  APIsForHandlingPUKsAkaServiceCodesApi,
  Configuration,
  type Middleware,
  PublicPasswordResetRESTServiceAPIApi
} from 'account-api'

// CSRF is only enabled in tests (see setupTests / TEST_USE_CSRF). The account
// management service has no security scheme in its OpenAPI spec, so KeyId,
// X-Line, CSRF and the bearer token are injected here, not by the generator.
const useCSRF = Boolean(
  (globalThis as typeof globalThis & { TEST_USE_CSRF?: boolean }).TEST_USE_CSRF
)

function basePath(): string {
  return globalThis.ENV_CONFIG?.SERVICE_HOSTS.ACCOUNT_MANAGEMENT
}

/**
 * Holt (und cached) den CSRF-Token für den Account-Management-Service.
 * Bleibt handgeschrieben, da `/csrf` nicht Teil des generierten Clients ist.
 */
export async function getCsrfAccountManagementToken(): Promise<CsrfResponse | null> {
  if (!useCSRF) return null

  const storedToken = sessionStorage.getItem('csrf-account-management-token')
  if (storedToken) {
    try {
      return JSON.parse(storedToken) as CsrfResponse
    } catch (error) {
      console.error('Fehler beim Parsen des gespeicherten CSRF-Tokens', error)
    }
  }

  return fetch(`${basePath()}/csrf`, {
    method: 'GET',
    headers: {
      KeyId: globalThis.ENV_CONFIG?.API_KEY,
      'Content-Type': 'application/json',
      'X-Line': 'AccountManagement'
    },
    credentials: 'include'
  })
    .then((res) => res.json())
    .then((data) => {
      sessionStorage.setItem('csrf-account-management-token', JSON.stringify(data))
      return data
    })
    .catch((error) => {
      console.error('Fehler beim Abrufen des CSRF-Tokens für Account Management', error)
      return null
    })
}

// pre-Middleware ergänzt den dynamischen CSRF-Header (Header-Name kommt aus der Response).
const csrfMiddleware: Middleware = {
  async pre({ url, init }) {
    if (!useCSRF) return { url, init }
    const csrf = await getCsrfAccountManagementToken()
    if (csrf) {
      init.headers = {
        ...(init.headers as Record<string, string>),
        [csrf.headerName]: csrf.token
      }
    }
    return { url, init }
  }
}

function buildConfiguration(opts: { withToken: boolean }): Configuration {
  return new Configuration({
    basePath: basePath(),
    headers: {
      KeyId: globalThis.ENV_CONFIG?.API_KEY,
      'X-Line': 'AccountManagement'
    },
    // Der Client setzt Authorization nur, wenn accessToken einen Wert liefert.
    accessToken: opts.withToken ? async () => (await getToken()) ?? '' : undefined,
    credentials: useCSRF ? 'include' : undefined,
    middleware: [csrfMiddleware]
  })
}

export const accountAuthenticationApi = () =>
  new AccountRESTServiceAPIApi(buildConfiguration({ withToken: false }))

export const accountActivationApi = () =>
  new AccountActivationAPIApi(buildConfiguration({ withToken: false }))

export const accountRestApi = (withToken: boolean) =>
  new AccountRESTServiceAPIApi(buildConfiguration({ withToken }))

export const pukApi = (withToken: boolean) =>
  new APIsForHandlingPUKsAkaServiceCodesApi(buildConfiguration({ withToken }))

export const emailChangeApi = () =>
  new APIsForChangingEmailOfAccountApi(buildConfiguration({ withToken: true }))

export const passwordResetApi = () =>
  new PublicPasswordResetRESTServiceAPIApi(buildConfiguration({ withToken: false }))
